import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, Download, Plus, UserCog } from "lucide-react";
import { useHorizontalScrollEdges } from "../../hooks/useHorizontalScrollEdges.js";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { useMe } from "../../context/MeContext.js";
import { Combobox } from "../../components/ui/combobox.js";
import { Button } from "../../components/ui/button.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { Badge } from "../../components/ui/badge.js";
import { Pagination } from "../../components/ui/pagination.js";
import { DropdownSelect } from "../../components/DropdownButton.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table.js";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog.js";
import { EmptyState, Field, ReviewKindBadge, fmtDate, toEmployeeOptions } from "./shared.js";
import TemporaryManagerModal from "./TemporaryManagerModal.js";
import type { ListMeta, ReviewListRow, ReviewType } from "./types.js";

type Tab = "my" | "team" | "all";
type ReviewKindFilter = "all" | "annual" | "temporary" | "probation";
type EmployeeFilter = "active" | "all";

interface Props {
  isHR: boolean;
  canCreate: boolean;
}

interface FiscalYearOption {
  value: number;
  label: string;
}

interface EmployeeOption {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  managerId?: string | null;
  reportingManagerId?: string | null;
  probationEndDate?: string | null;
  hireDate?: string | null;
}

// Whose reviews: current employees only (the default) or also former ones.
const EMPLOYEE_FILTER_OPTIONS: { value: EmployeeFilter; label: string }[] = [
  { value: "active", label: "Active employees" },
  { value: "all", label: "All employees" },
];

// Review type filter: all, regular annual, temporary-manager, on probation.
const REVIEW_KIND_OPTIONS: { value: ReviewKindFilter; label: string }[] = [
  { value: "all", label: "All reviews" },
  { value: "annual", label: "Annual" },
  { value: "temporary", label: "Temporary" },
  { value: "probation", label: "On probation" },
];

export default function ReviewListPage({ isHR, canCreate }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();

  const [tab, setTab] = useState<Tab>("my");
  const [rows, setRows] = useState<ReviewListRow[]>([]);
  const [meta, setMeta] = useState<ListMeta>({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fiscalYear, setFiscalYear] = useState<string>("");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [kind, setKind] = useState<ReviewKindFilter>("all");
  const [employeeFilter, setEmployeeFilter] = useState<EmployeeFilter>("active");
  const [years, setYears] = useState<FiscalYearOption[]>([]);
  const yearOptions = years.map((y) => ({ value: String(y.value), label: y.label }));

  // The row whose Temporary Support modal is open (grant / revoke / history).
  const [tempManagerFor, setTempManagerFor] = useState<ReviewListRow | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  // A new review is always the yearly one; probation is shown as a note below.
  const [openFormDropdown, setOpenFormDropdown] = useState<string | null>(null);
  const [form, setForm] = useState({ employeeId: "", fiscalYear: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    api
      .get<{ fiscalYears: FiscalYearOption[] }>("/performance/meta")
      .then((r) => {
        setYears(r.data.fiscalYears);
        const first = r.data.fiscalYears[0];
        if (first) {
          setFiscalYear(String(first.value));
          setForm((f) => ({ ...f, fiscalYear: String(first.value) }));
        }
      })
      .catch((e) => logError("ReviewListPage.meta", e));
  }, []);

  const load = useCallback(async () => {
    if (!fiscalYear) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tab,
        fiscalYear,
        page: String(page),
        limit: "20",
        employees: employeeFilter,
      });
      if (search.trim()) params.set("search", search.trim());
      if (kind !== "all") params.set("kind", kind);
      const res = await api.get<{ data: ReviewListRow[]; meta: ListMeta }>(
        `/performance/reviews?${params.toString()}`,
      );
      setRows(res.data.data);
      setMeta(res.data.meta);
    } catch (e) {
      logError("ReviewListPage.load", e);
      toast.show(errMsg(e) ?? "Could not load reviews", "error");
    } finally {
      setLoading(false);
    }
  }, [tab, fiscalYear, page, search, kind, employeeFilter, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reset to the first page whenever a filter changes, so a filter can never
  // land the user on an empty page 3 of a 1-page result.
  useEffect(() => {
    setPage(1);
  }, [tab, fiscalYear, search, kind, employeeFilter]);

  async function openCreate() {
    setFormError("");
    setShowCreate(true);
    try {
      const res = await api.get<{ data: EmployeeOption[] }>("/employees?limit=500");
      // Matches what the server lets each person create: HR picks anyone; a
      // manager picks themselves or their team — people whose manager or
      // reporting manager is them, the same rule as assigning goals.
      setEmployees(
        isHR
          ? res.data.data
          : res.data.data.filter(
              (e) => e.id === me.employeeId || e.managerId === me.employeeId || e.reportingManagerId === me.employeeId,
            ),
      );
    } catch (e) {
      logError("ReviewListPage.employees", e);
    }
  }

  async function createReview() {
    setSaving(true);
    setFormError("");
    try {
      const res = await api.post<{ id: string }>("/performance/reviews", {
        employeeId: form.employeeId,
        fiscalYear: Number(form.fiscalYear),
        reviewType: "ANNUAL" satisfies ReviewType,
      });
      setShowCreate(false);
      toast.show("Review created", "success");
      navigate(`/performance/reviews/${res.data.id}`);
    } catch (e) {
      logError("ReviewListPage.create", e);
      setFormError(errMsg(e) ?? "Could not create the review");
    } finally {
      setSaving(false);
    }
  }

  // The skills report as Excel. Authenticated blob download, so the
  // 401-refresh interceptor still applies.
  async function exportExcel() {
    try {
      const params = new URLSearchParams({ tab, fiscalYear, format: "xlsx", employees: employeeFilter });
      if (search.trim()) params.set("search", search.trim());
      if (kind !== "all") params.set("kind", kind);
      const res = await api.get(`/performance/reviews?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `performance-skills-${fiscalYear}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      logError("ReviewListPage.exportExcel", e);
      toast.show("Download failed", "error");
    }
  }

  // The table is wider than most viewports (fixed per-column widths, see the table
  // below), so the wrapper scrolls sideways. The scroll arrows show only while there is
  // somewhere left to go in that direction, and jump straight to that edge.
  const {
    ref: tableScrollRef, canScrollLeft, canScrollRight,
    scrollToStart: scrollTableLeft, scrollToEnd: scrollTableRight,
  } = useHorizontalScrollEdges([rows]);

  const tabs: { value: Tab; label: string }[] = [
    { value: "my", label: "My Reviews" },
    { value: "team", label: "My Team" },
    ...(isHR ? [{ value: "all" as Tab, label: "All Reviews" }] : []),
  ];

  // On probation = probation end date still ahead (hire date + 90 days), the
  // same rule the rest of the app uses.
  const picked = employees.find((e) => e.id === form.employeeId);
  const onProbation = !!picked?.probationEndDate && new Date(picked.probationEndDate) > new Date();

  return (
    <div>
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void exportExcel()}>
              <Download size={14} /> Export Excel
            </Button>
            {canCreate && (
              <Button size="sm" onClick={() => void openCreate()}>
                <Plus size={14} /> New review
              </Button>
            )}
          </div>
        </div>
      </Tabs>

      <div className="mt-5 mb-4 flex flex-wrap items-center gap-2">
        <SearchInput
          className="w-60"
          placeholder="Search name, email or employee ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <DropdownSelect
          value={fiscalYear}
          onChange={setFiscalYear}
          options={yearOptions}
          placeholder="Fiscal year"
          ariaLabel="Fiscal year"
          clearable={false}
          className="w-40 justify-between"
          open={openDropdown === "fiscalYear"}
          onToggle={() => setOpenDropdown((d) => (d === "fiscalYear" ? null : "fiscalYear"))}
          onClose={() => setOpenDropdown((d) => (d === "fiscalYear" ? null : d))}
        />

        <DropdownSelect
          value={kind}
          onChange={(v) => setKind(v as ReviewKindFilter)}
          options={REVIEW_KIND_OPTIONS}
          placeholder="All reviews"
          ariaLabel="Review type"
          clearable={false}
          className="w-44 justify-between"
          open={openDropdown === "reviewType"}
          onToggle={() => setOpenDropdown((d) => (d === "reviewType" ? null : "reviewType"))}
          onClose={() => setOpenDropdown((d) => (d === "reviewType" ? null : d))}
        />

        <DropdownSelect
          value={employeeFilter}
          onChange={(v) => setEmployeeFilter(v as EmployeeFilter)}
          options={EMPLOYEE_FILTER_OPTIONS}
          placeholder="Active employees"
          ariaLabel="Employees"
          clearable={false}
          className="w-44 justify-between"
          open={openDropdown === "employees"}
          onToggle={() => setOpenDropdown((d) => (d === "employees" ? null : "employees"))}
          onClose={() => setOpenDropdown((d) => (d === "employees" ? null : d))}
        />
      </div>

      {/* Twelve columns, each a fixed width (table-fixed): long text wraps
          inside its own column instead of stretching the table wider. Both
          edges are pinned (sticky) so they stay visible while scrolling
          sideways: Fiscal year on the left with a scroll-left control, and
          the View column on the right with a scroll-right control. */}
      <div ref={tableScrollRef} className="overflow-x-auto rounded-lg border border-surface-200 bg-surface-0">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 w-32 bg-surface-50 pl-4">
                <span className="flex items-center gap-1.5">
                  {canScrollLeft && (
                    <SpecularButton
                      onClick={scrollTableLeft}
                      aria-label="Scroll left back to the start"
                      size="sm"
                      className="w-6 px-0"
                    >
                      <ChevronsLeft size={13} />
                    </SpecularButton>
                  )}
                  Fiscal year
                </span>
              </TableHead>
              <TableHead className="w-32">Type</TableHead>
              <TableHead className="w-40">Employee</TableHead>
              <TableHead className="w-32">Employee ID</TableHead>
              <TableHead className="w-40">Job title</TableHead>
              <TableHead className="w-32">Manager</TableHead>
              <TableHead className="w-32">Department</TableHead>
              <TableHead className="w-16">Skills</TableHead>
              <TableHead className="w-36">Tenure</TableHead>
              <TableHead className="w-24">Final rating</TableHead>
              <TableHead className="w-36">HR Acknowledge</TableHead>
              <TableHead className="sticky right-0 z-10 w-28 bg-surface-50 pr-4 text-center">
                {canScrollRight && (
                  <SpecularButton
                    onClick={scrollTableRight}
                    aria-label="Scroll right to see more columns"
                    size="sm"
                    className="w-7 px-0"
                  >
                    <ChevronsRight size={14} />
                  </SpecularButton>
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} className="px-4 py-9 text-center text-[13px] text-surface-400">
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <EmptyState
                    message={
                      search.trim()
                        ? "No reviews match your search."
                        : "No reviews for the selected year yet."
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} className="group hover:bg-surface-50">
                  <TableCell className="sticky left-0 z-10 bg-surface-0 pl-4 font-medium group-hover:bg-surface-50">
                    {r.fiscalYearLabel}
                  </TableCell>
                  <TableCell>
                    <ReviewKindBadge temporary={!!r.isTemporary} onProbation={r.onProbation} />
                  </TableCell>
                  <TableCell className="font-medium break-words">{r.employee.name}</TableCell>
                  <TableCell className="text-surface-500">{r.employee.employeeCode}</TableCell>
                  <TableCell className="break-words">{r.employee.designation}</TableCell>
                  <TableCell className="break-words">{r.manager ?? "—"}</TableCell>
                  <TableCell className="break-words">{r.employee.department ?? "—"}</TableCell>
                  <TableCell>
                    <Badge tone="outline">{r.employee.skillCount}</Badge>
                  </TableCell>
                  <TableCell className="break-words text-surface-500">{r.employee.tenure}</TableCell>
                  <TableCell className="tabular-nums">
                    {r.managerFinalRating == null ? "" : r.managerFinalRating.toFixed(1)}
                  </TableCell>
                  <TableCell>
                    {r.hrAcknowledgementDate ? (
                      <Badge tone="success">{fmtDate(r.hrAcknowledgementDate)}</Badge>
                    ) : (
                      <Badge tone="neutral">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="sticky right-0 z-10 bg-surface-0 pr-4 group-hover:bg-surface-50">
                    <div className="flex items-center justify-end gap-1">
                      {/* Temporary Support (grant / revoke / history) on team rows,
                          per spec — never on My Reviews, where the row is your own,
                          and never on a row the viewer only sees AS the temporary
                          manager (they cannot grant/revoke on a delegated review). */}
                      {(tab === "team" || tab === "all") && !r.isTemporary && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Temporary manager"
                          aria-label={`Temporary manager for ${r.employee.name}`}
                          onClick={() => setTempManagerFor(r)}
                        >
                          <UserCog size={15} />
                        </Button>
                      )}
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/performance/reviews/${r.id}`}>View</Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!loading && rows.length > 0 && <Pagination meta={meta} onPageChange={setPage} />}

      <TemporaryManagerModal
        open={tempManagerFor != null}
        reviewId={tempManagerFor?.id ?? null}
        employeeId={tempManagerFor?.employee.id ?? null}
        employeeName={tempManagerFor?.employee.name ?? null}
        fiscalYear={tempManagerFor?.fiscalYear ?? null}
        onClose={() => setTempManagerFor(null)}
      />

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New performance review</DialogTitle>
            <DialogDescription>
              One yearly review per employee per fiscal year.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <Field
              label="Employee"
              hint={isHR ? undefined : "You can create a review for yourself or your team."}
            >
              <Combobox
                value={form.employeeId}
                onValueChange={(v) => setForm((f) => ({ ...f, employeeId: v }))}
                options={toEmployeeOptions(employees)}
                placeholder="Select an employee"
                searchPlaceholder="Search by name or employee ID"
                emptyText={isHR ? "No employee matches." : "No one on your team matches."}
              />
            </Field>

            {onProbation && picked && (
              <p className="rounded-md bg-[var(--color-info-bg-subtle)] px-3 py-2 text-[12px] text-[var(--color-info-fg)]">
                {picked.firstName} {picked.lastName} is on probation until {fmtDate(picked.probationEndDate)}
                {picked.hireDate ? ` (joined ${fmtDate(picked.hireDate)})` : ""}. Their annual review will show them as on probation, with the First 30 Days review and the probation decision.
              </p>
            )}

            <Field label="Fiscal year">
              <DropdownSelect
                value={form.fiscalYear}
                onChange={(v) => setForm((f) => ({ ...f, fiscalYear: v }))}
                options={years.map((y) => ({ value: String(y.value), label: y.label }))}
                placeholder="Fiscal year"
                ariaLabel="Fiscal year"
                clearable={false}
                className="w-full justify-between"
                open={openFormDropdown === "fiscalYear"}
                onToggle={() => setOpenFormDropdown((d) => (d === "fiscalYear" ? null : "fiscalYear"))}
                onClose={() => setOpenFormDropdown((d) => (d === "fiscalYear" ? null : d))}
              />
            </Field>

            {formError && <p className="text-[13px] text-[var(--color-error-fg)]">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button disabled={saving || !form.employeeId || !form.fiscalYear} onClick={() => void createReview()}>
              {saving ? "Creating…" : "Create review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
